import { useEffect, useMemo, useRef, useState } from 'react';

import { Input } from './Input';
import { Popover } from './Popover';

export interface BuscaItem {
  id: string;
  label: string;
  /** Texto secundário (ex.: nº humano, status) — também entra na busca. */
  sublabel?: string;
}

interface BuscaComboboxProps {
  id?: string;
  /** ID selecionado ('' = nenhum). */
  value: string;
  onChange: (id: string) => void;
  /** Lista completa em memória (filtrada por nome ao digitar). */
  items: BuscaItem[];
  placeholder?: string;
  emptyText?: string;
  /** Quantos itens exibir no máximo (default 30). */
  max?: number;
}

/**
 * Combobox genérico com **busca por nome** sobre uma lista em memória (digita →
 * filtra). Distinto do `ClienteV2Combobox` (busca server-side em `clientes`).
 * (O antigo `CatalogCombobox`, preso ao `useCatalogItems`, foi removido em
 * 2026-08-05 — zero consumidores; este componente é o sucessor.) Usado no
 * Vínculo da solicitação (FX-11) pros seletores de **Projeto** e **Deal**,
 * cujas listas já vêm carregadas.
 */
export function BuscaCombobox({
  id,
  value,
  onChange,
  items,
  placeholder = 'Buscar por nome…',
  emptyText = 'Nenhum resultado',
  max = 30,
}: BuscaComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selecionado = useMemo(() => items.find((i) => i.id === value), [items, value]);
  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? items.filter(
          (i) =>
            i.label.toLowerCase().includes(q) || (i.sublabel?.toLowerCase().includes(q) ?? false),
        )
      : items;
    return base.slice(0, max);
  }, [items, query, max]);

  const displayValue = open ? query : selecionado?.label ?? '';

  function commit(item: BuscaItem) {
    onChange(item.id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        size="sm"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
      />
      {/* CENSO-FLUTUANTE (esteira): era `absolute` — imune hoje só porque os
          usos vivem em página sem scroller; quebraria no dia em que o form
          virasse Dialog/Drawer. Migrado pro <Popover> canônico. */}
      <Popover
        aberto={open}
        gatilhoRef={containerRef}
        onFechar={() => {
          setOpen(false);
          setQuery('');
        }}
        larguraDoGatilho
        className="rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-lg"
      >
        <ul role="listbox" className="max-h-72 overflow-y-auto">
          {filtrados.length === 0 && (
            <li className="px-4 py-2 text-xs text-ink-3 dark:text-ink-dark-3 italic">{emptyText}</li>
          )}
          {filtrados.map((item) => (
            <li
              key={item.id}
              role="option"
              aria-selected={item.id === value}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(item);
              }}
              className="cursor-pointer px-4 py-2 text-sm text-ink-1 dark:text-ink-dark-1 hover:bg-paper-muted dark:hover:bg-paper-dark-muted"
            >
              {item.label}
              {item.sublabel ? (
                <span className="text-ink-3 dark:text-ink-dark-3"> · {item.sublabel}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}
